import { backendApi } from "./backendApi.rtkq";
import type { Plan } from "./plans.rtkq";

export interface AccountOwner {
  id: string;
  displayName: string;
  email: string;
}

export interface AccountInformation {
  phoneNumber?: string;
  businessName?: string;
  businessNumber?: string;
  city?: string;
  isProfileComplete?: boolean;
}

export interface Account {
  accountId: string;
  name?: string;
  ownerId?: string; // @deprecated - use owner.id instead, kept for backwards compatibility
  owner?: AccountOwner;
  accountInformation: AccountInformation;
  subscription: Subscription;
  creditBalance?: number; // @deprecated - use appCreditBalances instead, kept for backwards compatibility
  appCreditBalances?: {
    [appId: string]: number; // Per-app credit balances (e.g., { "scan-documents-extraction": 10, "ai-assistant": 10 })
  };
  appBonusCreditBalances?: {
    [appId: string]: number;
  };
  lastUsageDate?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  isSoftDeleted?: boolean;
  billingStatus?: string;
}

export type SubscriptionStatus = "active" | "past_due" | "cancelled" | "expired" | "incomplete";

export interface Subscription {
  plan: Plan;
  status: SubscriptionStatus;
  startDate: Date;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  trialStart?: Date;
  trialEnd?: Date;
  cancelledAt?: Date;
  cancelAtPeriodEnd: boolean;
  lastCreditReset?: Date;
  updatedAt: Date;
}

export interface UpdateAccountParams {
  accountId: string;
  updates: Partial<Omit<Account, "accountId" | "createdAt">>;
}

export interface DeleteAccountParams {
  accountId: string;
}

export interface GetAccountByIdParams {
  accountId: string;
}

export interface GetAccountsByOwnerParams {
  ownerId: string;
}

export interface UpdateCreditsParams {
  accountId: string;
  newCreditAmount: number;
  previousCreditAmount: number;
  creditDifference: number;
}

export interface CancelSubscriptionParams {
  accountId: string;
  immediate?: boolean;
  cancelledBy?: string;
  reason?: string;
}

export interface GetAppUsageParams {
  accountId: string;
  appId: string;
  periodStart: Date;
}

export interface AppUsageStats {
  totalCreditsConsumed: number;
  totalOperations: number;
}

export const accountsApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all accounts (super admin)
    getAccounts: builder.query<Account[], void>({
      query: () => "/admin/overview",
      transformResponse: (response: any) => {
        const offices = response.offices || response || [];
        return offices.map((office: any) => ({
          ...office,
          accountId: office.accountId || office.id,
          accountInformation: office.accountInformation ?? {},
          createdAt: office.createdAt ? new Date(office.createdAt) : undefined,
          updatedAt: office.updatedAt ? new Date(office.updatedAt) : undefined,
          lastUsageDate: office.lastUsageDate ? new Date(office.lastUsageDate) : undefined,
          subscription: office.subscription
            ? {
                ...office.subscription,
                startDate: office.subscription.startDate ? new Date(office.subscription.startDate) : undefined,
                currentPeriodStart: office.subscription.currentPeriodStart ? new Date(office.subscription.currentPeriodStart) : undefined,
                currentPeriodEnd: office.subscription.currentPeriodEnd ? new Date(office.subscription.currentPeriodEnd) : undefined,
                trialStart: office.subscription.trialStart ? new Date(office.subscription.trialStart) : undefined,
                trialEnd: office.subscription.trialEnd ? new Date(office.subscription.trialEnd) : undefined,
                cancelledAt: office.subscription.cancelledAt ? new Date(office.subscription.cancelledAt) : undefined,
                lastCreditReset: office.subscription.lastCreditReset ? new Date(office.subscription.lastCreditReset) : undefined,
              }
            : office.subscription,
        }));
      },
      providesTags: ["Accounts", { type: "Accounts", id: "list" }],
    }),

    // Get account by ID — maps flat OfficeEntity to nested Account shape
    getAccountById: builder.query<Account | null, GetAccountByIdParams>({
      query: ({ accountId }) => `/offices/by-id/${accountId}`,
      transformResponse: (response: any) => {
        if (!response) return null;
        return {
          accountId: response.id,
          name: response.name,
          owner: response.owner,
          accountInformation: response.accountInformation ?? {},
          creditBalance: response.creditBalance,
          appCreditBalances: response.appCreditBalances,
          appBonusCreditBalances: response.appBonusCreditBalances,
          lastUsageDate: response.lastUsageDate ? new Date(response.lastUsageDate) : undefined,
          createdAt: response.createdAt ? new Date(response.createdAt) : undefined,
          updatedAt: response.updatedAt ? new Date(response.updatedAt) : undefined,
          isSoftDeleted: response.isSoftDeleted,
          subscription: {
            plan: response.subscriptionPlan || { includedApps: [] },
            status: response.subscriptionStatus || "active",
            startDate: response.subscriptionStartDate ? new Date(response.subscriptionStartDate) : new Date(),
            currentPeriodStart: response.subscriptionStartDate ? new Date(response.subscriptionStartDate) : new Date(),
            currentPeriodEnd: response.subscriptionCurrentPeriodEnd ? new Date(response.subscriptionCurrentPeriodEnd) : new Date(),
            cancelAtPeriodEnd: response.subscriptionCancelAtPeriodEnd || false,
            updatedAt: response.updatedAt ? new Date(response.updatedAt) : new Date(),
          },
        };
      },
      providesTags: (result, _error, { accountId }) => (result ? [{ type: "Accounts", id: accountId }] : []),
    }),

    // Update existing account
    updateAccount: builder.mutation<{ success: boolean }, UpdateAccountParams>({
      query: ({ accountId, updates }) => ({
        url: `/offices/${accountId}`,
        method: "PUT",
        body: updates,
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (result, _error, { accountId }) => (result ? [{ type: "Accounts", id: accountId }, "Accounts"] : []),
    }),

    // Delete account (soft delete via PUT)
    deleteAccount: builder.mutation<{ success: boolean }, DeleteAccountParams>({
      query: ({ accountId }) => ({
        url: `/offices/${accountId}`,
        method: "PUT",
        body: { isSoftDeleted: true },
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: ["Accounts"],
    }),

    // Get accounts by owner — filters from admin overview
    getAccountsByOwner: builder.query<Account[], GetAccountsByOwnerParams>({
      query: () => "/admin/overview",
      transformResponse: (response: any, _meta, { ownerId }: GetAccountsByOwnerParams) => {
        const offices = response.offices || response || [];
        return offices
          .filter((office: any) => office.ownerId === ownerId || office.owner?.id === ownerId)
          .map((office: any) => ({
            ...office,
            accountId: office.accountId || office.id,
            accountInformation: office.accountInformation ?? {},
            creditBalance: office.creditBalance,
            appCreditBalances: office.appCreditBalances,
            createdAt: office.createdAt ? new Date(office.createdAt) : undefined,
            updatedAt: office.updatedAt ? new Date(office.updatedAt) : undefined,
            lastUsageDate: office.lastUsageDate ? new Date(office.lastUsageDate) : undefined,
            subscription: office.subscription
              ? {
                  ...office.subscription,
                  startDate: office.subscription.startDate ? new Date(office.subscription.startDate) : undefined,
                  currentPeriodStart: office.subscription.currentPeriodStart ? new Date(office.subscription.currentPeriodStart) : undefined,
                  currentPeriodEnd: office.subscription.currentPeriodEnd ? new Date(office.subscription.currentPeriodEnd) : undefined,
                  trialStart: office.subscription.trialStart ? new Date(office.subscription.trialStart) : undefined,
                  trialEnd: office.subscription.trialEnd ? new Date(office.subscription.trialEnd) : undefined,
                  cancelledAt: office.subscription.cancelledAt ? new Date(office.subscription.cancelledAt) : undefined,
                  lastCreditReset: office.subscription.lastCreditReset ? new Date(office.subscription.lastCreditReset) : undefined,
                }
              : {
                  plan: office.subscriptionPlan || { includedApps: [] },
                  status: office.subscriptionStatus || "active",
                  startDate: office.subscriptionStartDate ? new Date(office.subscriptionStartDate) : new Date(),
                  currentPeriodStart: office.subscriptionStartDate ? new Date(office.subscriptionStartDate) : new Date(),
                  currentPeriodEnd: office.subscriptionCurrentPeriodEnd ? new Date(office.subscriptionCurrentPeriodEnd) : new Date(),
                  cancelAtPeriodEnd: office.subscriptionCancelAtPeriodEnd || false,
                  updatedAt: office.updatedAt ? new Date(office.updatedAt) : new Date(),
                },
          }));
      },
      providesTags: (result, _error, { ownerId }) => (result ? [{ type: "Accounts", id: `owner-${ownerId}` }] : []),
    }),

    // Update subscription
    updateAccountSubscription: builder.mutation<{ success: boolean }, { accountId: string; newPlanId: string }>({
      query: ({ accountId, newPlanId }) => ({
        url: `/offices/${accountId}/subscription`,
        method: "PUT",
        body: { planId: newPlanId },
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (result, _error, { accountId }) =>
        result
          ? [
              { type: "Accounts", id: accountId },
              "Accounts",
              { type: "Accounts", id: "list" },
              "Dashboard",
            ]
          : [],
    }),

    // Update credits
    updateCredits: builder.mutation<{ success: boolean }, UpdateCreditsParams>({
      query: ({ accountId, newCreditAmount, previousCreditAmount, creditDifference }) => ({
        url: `/offices/${accountId}/credits`,
        method: "PUT",
        body: { newAmount: newCreditAmount, newCreditAmount, previousCreditAmount, creditDifference },
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (result, _error, { accountId }) =>
        result ? [{ type: "Accounts", id: accountId }, "Accounts", "Dashboard"] : [],
    }),

    // Cancel subscription
    cancelSubscription: builder.mutation<{ success: boolean }, CancelSubscriptionParams>({
      query: ({ accountId, immediate, cancelledBy, reason }) => ({
        url: `/offices/${accountId}/subscription/cancel`,
        method: "POST",
        body: { immediate, cancelledBy, reason },
      }),
      transformResponse: () => ({ success: true }),
      invalidatesTags: (result, _error, { accountId }) =>
        result
          ? [
              { type: "Accounts", id: accountId },
              "Accounts",
              { type: "Accounts", id: "list" },
              "Dashboard",
            ]
          : [],
    }),

    // Get app usage stats for the current billing period
    getAppUsageStats: builder.query<AppUsageStats, GetAppUsageParams>({
      query: ({ accountId, appId, periodStart }) => ({
        url: "/usage/logs",
        params: {
          accountId,
          appId,
          periodStart: periodStart.toISOString(),
        },
      }),
      transformResponse: (response: any) => {
        // Backend returns usage logs; aggregate client-side
        const logs = response.logs || response || [];
        let totalCreditsConsumed = 0;
        logs.forEach((log: any) => {
          totalCreditsConsumed += log.creditsConsumed || 0;
        });
        return {
          totalCreditsConsumed,
          totalOperations: logs.length,
        };
      },
      providesTags: (_result, _error, { accountId, appId }) => [
        { type: "Accounts", id: `usage-${accountId}-${appId}` },
      ],
    }),
  }),
});

export const {
  useGetAccountsQuery,
  useGetAccountByIdQuery,
  useUpdateAccountMutation,
  useDeleteAccountMutation,
  useGetAccountsByOwnerQuery,
  useUpdateAccountSubscriptionMutation,
  useUpdateCreditsMutation,
  useCancelSubscriptionMutation,
  useGetAppUsageStatsQuery,
} = accountsApi;
